import { formatMoney } from '@/utils/currency';

const PAGE_CSS_WIDTH = 794;
/** Rows that fit on first page (header is taller). */
const ROWS_FIRST_PAGE = 14;
/** Rows on continuation pages. */
const ROWS_NEXT_PAGE = 22;

function escapeHtml(s: unknown) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function sanitize(value: unknown): string {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';
  if (
    /\b(const|let|var|function|import|export|return)\b/.test(s) ||
    /[{};=<>]/.test(s) ||
    /subtotalDisplay|promoTotals|frappe\./i.test(s)
  ) {
    return '';
  }
  return s;
}

function chunkItems<T>(items: T[]): T[][] {
  if (!items.length) return [[]];
  const first = items.slice(0, ROWS_FIRST_PAGE);
  const rest = items.slice(ROWS_FIRST_PAGE);
  const pages: T[][] = [first];
  for (let i = 0; i < rest.length; i += ROWS_NEXT_PAGE) {
    pages.push(rest.slice(i, i + ROWS_NEXT_PAGE));
  }
  return pages;
}

async function optimizeLogo(src?: string): Promise<string> {
  if (!src) return '';
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.crossOrigin = 'anonymous';
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = src;
    });
    const maxW = 160;
    const maxH = 56;
    const scale = Math.min(1, maxW / img.width, maxH / img.height);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return src;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', 0.92);
  } catch {
    return src;
  }
}

function formatUkDate(value?: string) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return escapeHtml(String(value).split(' ')[0]);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

type QuoteTotals = {
  subtotal: number;
  vat: number;
  total: number;
  discount: number;
  discountLabel: string;
};

function getTotals(quotationDetails: any): QuoteTotals {
  const subtotal = Number(quotationDetails?.net_total ?? quotationDetails?.financials?.subtotal ?? 0) || 0;
  const vat = Number(quotationDetails?.total_taxes_and_charges ?? quotationDetails?.financials?.vat ?? 0) || 0;
  const total = Number(quotationDetails?.grand_total ?? quotationDetails?.financials?.total ?? subtotal) || 0;
  const discount =
    Number(
      quotationDetails?.discount_amount ??
        quotationDetails?.financials?.discount_amount ??
        quotationDetails?.financials?.discount ??
        0,
    ) ||
    (subtotal + vat > total ? Math.round((subtotal + vat - total) * 100) / 100 : 0);

  let discPct = quotationDetails?.discount_percent ?? quotationDetails?.financials?.discount_percent;
  if (discPct == null && quotationDetails?.notes) {
    const match =
      String(quotationDetails.notes).match(/Discount(?:\s*Rate)?:\s*([\d.]+)%/i) ||
      String(quotationDetails.notes).match(/Discount\s*\((?:%|percentage)\):\s*([\d.]+)/i);
    if (match) discPct = parseFloat(match[1]);
  }
  const discountLabel = discPct != null && discPct > 0 ? `Discount (${discPct}%)` : 'Discount';
  return { subtotal, vat, total, discount, discountLabel };
}

function buildQuotePage(
  quotationDetails: any,
  profile: any,
  pageItems: any[],
  itemOffset: number,
  pageIndex: number,
  pageCount: number,
  isLastPage: boolean,
  logoSrc: string,
  totals: QuoteTotals,
) {
  const companyName =
    sanitize(profile?.company_name || profile?.company?.name || profile?.company) || 'Company Name';
  const companyAddress = sanitize(profile?.address);
  const companyPhone = sanitize(profile?.phone || profile?.mobile || profile?.contact_no);
  const customerName = sanitize(quotationDetails?.customer_name || quotationDetails?.customer) || 'Customer';
  const customerContact = sanitize(quotationDetails?.contact_display);
  const quotationDate = formatUkDate(quotationDetails?.creation || quotationDetails?.invoice_date);
  const validUntil = quotationDetails?.valid_till
    ? formatUkDate(quotationDetails.valid_till)
    : '—';
  const preparer = sanitize(quotationDetails?.owner || profile?.full_name) || 'Staff';
  const footerText =
    typeof window !== 'undefined' && window.location.hostname.includes('kashly')
      ? 'Powered by Kashly'
      : 'Powered by Saledesk';
  const logoHtml = logoSrc
    ? `<img src="${escapeHtml(logoSrc)}" alt="" style="max-height:48px;max-width:140px;object-fit:contain;margin-bottom:6px;" />`
    : '';

  const rows = pageItems
    .map((item, idx) => {
      const lineNo = itemOffset + idx + 1;
      const name = item.item_name || item.name || '';
      const code = item.item_code || '';
      const comment = String(item.line_comment || '').trim();
      const qty = item.qty ?? item.quantity ?? 0;
      const rate = Number(item.rate) || 0;
      const amount = Number(item.amount) || qty * rate;
      return `
        <tr style="border-bottom:1px solid #e5e7eb;">
          <td style="padding:8px 4px;color:#111;vertical-align:top;">
            <div style="font-weight:600;font-size:11px;">
              <span style="color:#9ca3af;font-weight:500;margin-right:4px;">${lineNo}.</span>${escapeHtml(name)}
            </div>
            ${comment ? `<div style="font-size:10px;color:#444;font-style:italic;margin-top:2px;">${escapeHtml(comment)}</div>` : ''}
            ${code ? `<div style="font-size:9px;color:#888;font-family:monospace;margin-top:1px;">${escapeHtml(code)}</div>` : ''}
          </td>
          <td style="padding:8px 4px;text-align:center;color:#333;font-size:11px;">${escapeHtml(qty)}</td>
          <td style="padding:8px 4px;text-align:right;color:#333;font-size:11px;">${escapeHtml(formatMoney(rate))}</td>
          <td style="padding:8px 4px;text-align:right;color:#111;font-weight:600;font-size:11px;">${escapeHtml(formatMoney(amount))}</td>
        </tr>`;
    })
    .join('');

  const totalsHtml = isLastPage
    ? `
      <div style="display:flex;justify-content:flex-end;margin-top:16px;margin-bottom:8px;">
        <table style="width:280px;font-size:11px;border-collapse:collapse;">
          <tr>
            <td style="padding:5px 0;color:#555;">Subtotal</td>
            <td style="padding:5px 0;text-align:right;font-weight:500;color:#111;">${escapeHtml(formatMoney(totals.subtotal))}</td>
          </tr>
          ${
            totals.vat > 0
              ? `<tr>
            <td style="padding:5px 0;color:#555;">Tax (VAT)</td>
            <td style="padding:5px 0;text-align:right;font-weight:500;color:#111;">${escapeHtml(formatMoney(totals.vat))}</td>
          </tr>`
              : ''
          }
          ${
            totals.discount > 0
              ? `<tr>
            <td style="padding:5px 0;color:#059669;">${escapeHtml(totals.discountLabel)}</td>
            <td style="padding:5px 0;text-align:right;font-weight:500;color:#059669;">− ${escapeHtml(formatMoney(totals.discount))}</td>
          </tr>`
              : ''
          }
          <tr>
            <td style="padding:10px 0;color:#111;font-weight:700;font-size:13px;border-top:2px solid #111;">Total</td>
            <td style="padding:10px 0;text-align:right;font-weight:700;font-size:13px;color:#111;border-top:2px solid #111;">${escapeHtml(formatMoney(totals.total))}</td>
          </tr>
        </table>
      </div>`
    : `
      <div style="margin-top:12px;font-size:10px;color:#6b7280;text-align:right;">
        Continued on next page…
      </div>`;

  const headerBlock =
    pageIndex === 0
      ? `
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:18px;">
          <div style="width:50%;">
            ${logoHtml}
            <div style="font-size:18px;font-weight:700;color:#111;margin-bottom:3px;">${escapeHtml(companyName)}</div>
            <div style="font-size:10px;color:#555;line-height:1.35;">
              ${companyAddress ? `${escapeHtml(companyAddress)}<br>` : ''}
              ${companyPhone ? escapeHtml(companyPhone) : ''}
            </div>
          </div>
          <div style="width:48%;text-align:right;">
            <div style="font-size:24px;font-weight:300;color:#111;letter-spacing:1px;margin-bottom:8px;">QUOTATION</div>
            <table style="width:auto;font-size:10px;text-align:right;margin-left:auto;">
              ${
                quotationDetails?.name
                  ? `<tr>
                <td style="color:#666;padding-bottom:3px;padding-right:16px;">Quote ID</td>
                <td style="font-weight:700;font-family:monospace;color:#111;padding-bottom:3px;">${escapeHtml(quotationDetails.name)}</td>
              </tr>`
                  : ''
              }
              <tr>
                <td style="color:#666;padding-bottom:3px;padding-right:16px;">Date</td>
                <td style="font-weight:600;color:#111;padding-bottom:3px;">${quotationDate}</td>
              </tr>
              <tr>
                <td style="color:#666;padding-bottom:3px;padding-right:16px;">Valid Until</td>
                <td style="font-weight:600;color:#111;padding-bottom:3px;">${validUntil}</td>
              </tr>
            </table>
          </div>
        </div>

        <div style="margin-bottom:16px;padding-top:12px;border-top:2px solid #f3f4f6;">
          <div style="font-size:9px;font-weight:700;color:#888;text-transform:uppercase;margin-bottom:4px;">Quotation For</div>
          <div style="font-size:13px;font-weight:600;color:#111;margin-bottom:2px;">${escapeHtml(customerName)}</div>
          ${customerContact ? `<div style="font-size:10px;color:#555;line-height:1.35;">${escapeHtml(customerContact)}</div>` : ''}
        </div>`
      : `
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;padding-bottom:8px;border-bottom:1px solid #e5e7eb;">
          <div style="font-size:12px;font-weight:700;color:#111;">${escapeHtml(companyName)} — Quotation continued</div>
          <div style="font-size:10px;font-family:monospace;color:#555;">${escapeHtml(quotationDetails?.name || '')}</div>
        </div>`;

  return `
    <section class="quotation-page" data-page="${pageIndex + 1}" data-pages="${pageCount}" style="width:${PAGE_CSS_WIDTH}px;box-sizing:border-box;padding:36px 40px 28px;background:#fff;color:#333;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;display:flex;flex-direction:column;min-height:1080px;">
      <div style="flex:1 1 auto;">
        ${headerBlock}

        <table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:8px;">
          <thead>
            <tr style="border-bottom:2px solid #e5e7eb;">
              <th style="text-align:left;padding:7px 4px;color:#444;font-weight:600;width:55%;font-size:9px;text-transform:uppercase;">Description</th>
              <th style="text-align:center;padding:7px 4px;color:#444;font-weight:600;width:15%;font-size:9px;text-transform:uppercase;">Qty</th>
              <th style="text-align:right;padding:7px 4px;color:#444;font-weight:600;width:15%;font-size:9px;text-transform:uppercase;">Price</th>
              <th style="text-align:right;padding:7px 4px;color:#444;font-weight:600;width:15%;font-size:9px;text-transform:uppercase;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${rows || `<tr><td colspan="4" style="padding:16px;text-align:center;">No items</td></tr>`}
          </tbody>
        </table>

        ${totalsHtml}
      </div>

      <div style="padding-top:10px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;flex-shrink:0;font-size:9px;color:#888;">
        <div>Prepared by: <span style="font-weight:600;color:#555;">${escapeHtml(preparer)}</span></div>
        <div style="font-weight:700;color:#111;">Page ${pageIndex + 1} of ${pageCount}</div>
        <div style="color:#aaa;">${escapeHtml(footerText)}</div>
      </div>
    </section>
  `;
}

/** Multi-page A4 HTML for quotation (PDF + print). */
export async function generateQuotationHtml(quotationDetails: any, profile: any): Promise<string> {
  const items = quotationDetails?.items || [];
  const chunks = chunkItems(items);
  const pageCount = chunks.length;
  const totals = getTotals(quotationDetails);
  const logoSrc = await optimizeLogo(profile?.company_logo);
  let offset = 0;
  const pages = chunks.map((chunk, pageIdx) => {
    const html = buildQuotePage(
      quotationDetails,
      profile,
      chunk,
      offset,
      pageIdx,
      pageCount,
      pageIdx === pageCount - 1,
      logoSrc,
      totals,
    );
    offset += chunk.length;
    return html;
  });
  return `<div id="quotation-pdf-root">${pages.join('')}</div>`;
}

/** Sync single-page HTML kept for any callers that expect a string immediately — prefer generateQuotationHtml. */
export function generateQuotationHtmlSync(quotationDetails: any, profile: any): string {
  // Fallback: first page only without logo optimise (legacy)
  const items = quotationDetails?.items || [];
  const chunks = chunkItems(items);
  const totals = getTotals(quotationDetails);
  let offset = 0;
  return `<div id="quotation-pdf-root">${chunks
    .map((chunk, pageIdx) => {
      const html = buildQuotePage(
        quotationDetails,
        profile,
        chunk,
        offset,
        pageIdx,
        chunks.length,
        pageIdx === chunks.length - 1,
        profile?.company_logo || '',
        totals,
      );
      offset += chunk.length;
      return html;
    })
    .join('')}</div>`;
}

function asQuoteList(quotationDetails: any | any[]): any[] {
  if (Array.isArray(quotationDetails)) return quotationDetails.filter(Boolean);
  return quotationDetails ? [quotationDetails] : [];
}

export async function generateQuotationPdfDoc(quotationDetails: any | any[], profile: any) {
  const [html2canvas, jsPDF] = await Promise.all([
    import('html2canvas').then((m) => m.default),
    import('jspdf').then((m) => m.default),
  ]);

  const quotes = asQuoteList(quotationDetails);
  if (!quotes.length) throw new Error('No quotations selected');

  // One HTML tree with pages for every quote (page N of M is per quote)
  const parts: string[] = [];
  for (const q of quotes) {
    const html = await generateQuotationHtml(q, profile);
    parts.push(html.replace(/^<div id="quotation-pdf-root">/, '').replace(/<\/div>$/, ''));
  }
  const htmlContent = `<div id="quotation-pdf-root">${parts.join('')}</div>`;

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '0';
  container.style.zIndex = '-1';
  container.innerHTML = htmlContent;
  document.body.appendChild(container);

  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const pages = Array.from(container.querySelectorAll('.quotation-page')) as HTMLElement[];
    let firstJpeg = '';

    for (let i = 0; i < pages.length; i++) {
      const canvas = await html2canvas(pages[i], {
        scale: 2.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: PAGE_CSS_WIDTH,
        windowWidth: PAGE_CSS_WIDTH,
        logging: false,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      if (i === 0) firstJpeg = imgData;
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      if (i > 0) pdf.addPage();
      const drawH = Math.min(imgHeight, pageHeight);
      const drawW = imgWidth * (drawH / imgHeight);
      const x = (pageWidth - drawW) / 2;
      pdf.addImage(imgData, 'JPEG', x, 0, drawW, drawH, undefined, 'MEDIUM');
    }

    (pdf as any)._imgData = firstJpeg;
    (pdf as any)._quoteCount = quotes.length;
    (pdf as any)._quoteNames = quotes.map((q) => q?.name).filter(Boolean);
    return pdf;
  } finally {
    document.body.removeChild(container);
  }
}

export async function downloadQuotationPdf(quotationDetails: any | any[], profile: any) {
  const quotes = asQuoteList(quotationDetails);
  const pdf = await generateQuotationPdfDoc(quotes, profile);
  const stamp = new Date().toISOString().slice(0, 10);
  const label =
    quotes.length === 1
      ? `Quotation_${quotes[0]?.name || 'Download'}.pdf`
      : `Quotations_bulk_${quotes.length}_${stamp}.pdf`;
  pdf.save(label);
}

export async function printQuotation(quotationDetails: any | any[], profile: any) {
  const pdf = await generateQuotationPdfDoc(quotationDetails, profile);

  try {
    const { usePrinterStore } = await import('@/store/printerStore');
    const { settings } = usePrinterStore.getState();
    if (settings.usePrintHelper && settings.windowsPrinterName) {
      const { printImageViaHelper } = await import('@/services/printHelper');
      // Multi-page: fall through to browser PDF print for full document
      if ((pdf as any).internal?.getNumberOfPages?.() > 1) {
        /* use blob below */
      } else {
        const imgData = (pdf as any)._imgData || '';
        const imgBase64 = imgData.includes(',')
          ? imgData.split(',')[1]
          : pdf.output('datauristring').split(',')[1];
        await printImageViaHelper(settings.windowsPrinterName, imgBase64);
        const { default: toast } = await import('react-hot-toast');
        toast.success(`Quotation sent to ${settings.windowsPrinterName}`);
        return;
      }
    }
  } catch (helperErr) {
    console.warn('Silent Print Helper quotation print failed, falling back to browser tab', helperErr);
  }

  const blob = pdf.output('blob');
  const blobUrl = URL.createObjectURL(blob);
  const newWin = window.open(blobUrl, '_blank');
  if (newWin) {
    newWin.focus();
  } else {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = blobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (e) {
        console.error('Print iframe error', e);
      }
    };
  }
}
